import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "~/components/ui";
import { useReferenceFileEdit } from "~/features/references/hooks";
import { type ReferenceFileEditFormData, zReferenceFileEditFormData } from "~/features/references/types";

const FORM_ID = "reference-file-edit-form";

interface ReferenceFileEditDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  referenceId: string;
  referenceFileId: string;
  folioOffset: number;
}

export function ReferenceFileEditDialog({
  open,
  onOpenChange,
  referenceId,
  referenceFileId,
  folioOffset,
}: ReferenceFileEditDialogProps) {
  const { t } = useTranslation();
  const form = useForm<ReferenceFileEditFormData>({
    resolver: zodResolver(zReferenceFileEditFormData),
    defaultValues: { folioOffset: String(folioOffset) },
  });
  const { submit, processing } = useReferenceFileEdit({
    referenceId,
    referenceFileId,
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{`${t("ui.edit")} ${t("model.referenceFile.modelName")}`}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form id={FORM_ID} onSubmit={form.handleSubmit(submit)}>
            <FormField
              control={form.control}
              name="folioOffset"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("model.referenceFile.folioOffset")}</FormLabel>
                  <FormControl>
                    <Input type="number" inputMode="numeric" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={processing}>
            {t("ui.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
